import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpError } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { t } from '@web/i18n';
import { meRepository, type Me, type MeUser, type UpdateMeInput } from '@web/data/repositories';
import { useSnackbar } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

export interface ProfileFormValues {
  displayName: string;
  phone: string;
  whatsapp: string;
}

function toFormValues(me: Me | undefined | null): ProfileFormValues {
  return {
    displayName: me?.displayName ?? me?.name ?? '',
    phone: me?.phone ?? '',
    whatsapp: me?.whatsapp ?? '',
  };
}

export function diffPatch(initial: ProfileFormValues, next: ProfileFormValues): UpdateMeInput {
  const patch: UpdateMeInput = {};
  (Object.keys(next) as (keyof ProfileFormValues)[]).forEach((key) => {
    const a = initial[key].trim();
    const b = next[key].trim();
    if (a === b) return;
    // null clears the field; empty string never reaches the wire.
    patch[key] = b === '' ? null : b;
  });
  return patch;
}

const errorMessage = (err: unknown): string => {
  if (err instanceof HttpError) return err.message;
  if (err instanceof Error) return err.message;
  return t('profile.toast.error');
};

export function useProfilePage() {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const { data: me = null } = useQuery({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });

  const initialValues = toFormValues(me);

  const mutation = useMutation({
    mutationFn: (input: UpdateMeInput) => meRepository.patchMe(input),
    onSuccess: async (updated: MeUser) => {
      queryClient.setQueryData<Me | undefined>(queryKeys.me.all, (prev) =>
        prev ? { ...prev, ...updated } : prev,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.me.all });
      showSnackbar({ severity: 'success', message: t('profile.toast.saved') });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: errorMessage(err) });
    },
  });

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      const patch = diffPatch(initialValues, value);
      if (Object.keys(patch).length === 0) return;
      await mutation.mutateAsync(patch);
      form.reset(value);
    },
  });

  return {
    isReady: me !== null,
    me,
    form,
    initialValues,
    isSaving: mutation.isPending,
    discard: () => {
      form.reset(initialValues);
    },
    save: () => {
      fireAndForget(form.handleSubmit());
    },
  };
}
